// Libs
import { Box, Collapse, List, ListItemButton, ListItemIcon, Stack, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
// Components, Layouts, Pages
// Others
import { EMPTY_STRING } from '~/utils/constants/common';
import { hasActiveChild } from '~/utils/helper/base';
import { IRouteModel } from '~/utils/interface/base';
import {
  CLIENT_PERMISSIONS,
  EQUIPMENT_PERMISSIONS,
  LEAD_PERMISSIONS,
  PACKAGE_PERMISSIONS,
  PRODUCT_PERMISSIONS,
  QUARANTINE_PERMISSIONS,
  TRAILER_PERMISSIONS,
  TRUCK_PERMISSIONS,
} from '~/utils/constants/permissions';
import { adminRoute } from '~/utils/routes/roleRoutes/admin';
import { usePermissions } from '~/utils/hooks/userPermissions';
// Styles, images, icons
import styles from './MenuItem.module.scss';

type Props = {
  menuItem: IRouteModel;
};

const cx = classNames.bind(styles);

const MenuItem = (props: Props) => {
  //#region Destructuring Props
  const { path, name = EMPTY_STRING, index, icon, activeIcon, children } = props.menuItem;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/sidebar', 'common/base']);
  const location = useLocation();

  const { canAllLeads, canCreateLeads, canViewLeads, canDetailLeads, canEditLeads, canDeleteLeads } =
    usePermissions(LEAD_PERMISSIONS);
  const { canAllClients, canCreateClients, canViewClients, canDetailClients, canEditClients, canDeleteClients } =
    usePermissions(CLIENT_PERMISSIONS);
  const { canAllProducts, canCreateProducts, canViewProducts, canDetailProducts, canEditProducts, canDeleteProducts } =
    usePermissions(PRODUCT_PERMISSIONS);
  const { canAllPackages, canCreatePackages, canViewPackages, canDetailPackages, canEditPackages, canDeletePackages } =
    usePermissions(PACKAGE_PERMISSIONS);
  const {
    canAllQuarantines,
    canCreateQuarantines,
    canViewQuarantines,
    canDetailQuarantines,
    canEditQuarantines,
    canDeleteQuarantines,
  } = usePermissions(QUARANTINE_PERMISSIONS);
  usePermissions(TRUCK_PERMISSIONS);
  const { canAllTrailers, canCreateTrailers, canViewTrailers, canDetailTrailers, canEditTrailers, canDeleteTrailers } =
    usePermissions(TRAILER_PERMISSIONS);
  const {
    canAllEquipments,
    canCreateEquipments,
    canViewEquipments,
    canDetailEquipments,
    canEditEquipments,
    canDeleteEquipments,
  } = usePermissions(EQUIPMENT_PERMISSIONS);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [openSubmenu, setOpenSubmenu] = useState<boolean>(false);
  const [hasChildActive, setHasChildActive] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  const isSubmenu: boolean = useMemo(() => (children ? children?.length > 0 : false), [children]);
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!props.menuItem || !children) return;

    const isActive = hasActiveChild(props.menuItem, location.pathname);
    setHasChildActive(isActive);
  }, [location.pathname]);
  //#endregion Implement Hook

  //#region Handle Function
  const toggleSubmenu = () => {
    setOpenSubmenu(!openSubmenu);
  };

  const handleShowSideBarSubmenu = (path: string): boolean => {
    switch (path) {
      case adminRoute.leads:
        return canAllLeads || canCreateLeads || canViewLeads || canDetailLeads || canEditLeads || canDeleteLeads;

      case adminRoute.clients:
        return (
          canAllClients || canCreateClients || canViewClients || canDetailClients || canEditClients || canDeleteClients
        );

      case adminRoute.product:
        return (
          canAllProducts ||
          canCreateProducts ||
          canViewProducts ||
          canDetailProducts ||
          canEditProducts ||
          canDeleteProducts
        );

      case adminRoute.package:
        return (
          canAllPackages ||
          canCreatePackages ||
          canViewPackages ||
          canDetailPackages ||
          canEditPackages ||
          canDeletePackages
        );

      case adminRoute.quarantines:
        return (
          canAllQuarantines ||
          canCreateQuarantines ||
          canViewQuarantines ||
          canDetailQuarantines ||
          canEditQuarantines ||
          canDeleteQuarantines
        );

      case adminRoute.trailers:
        return (
          canAllTrailers ||
          canCreateTrailers ||
          canViewTrailers ||
          canDetailTrailers ||
          canEditTrailers ||
          canDeleteTrailers
        );

      case adminRoute.equipments:
        return (
          canAllEquipments ||
          canCreateEquipments ||
          canViewEquipments ||
          canDetailEquipments ||
          canEditEquipments ||
          canDeleteEquipments
        );

      default:
        return true;
    }
  };
  //#endregion Handle Function

  return (
    <>
      {/* Normal Menu */}
      {!isSubmenu && (
        <NavLink to={path} end={index} id='menuItemComponent'>
          {({ isActive }) => (
            <ListItemButton className={cx('menuBtn', { active: isActive })} selected={isActive && hasChildActive}>
              <ListItemIcon className={cx('menuIconWrap')}>
                <img
                  src={isActive ? activeIcon : icon}
                  alt={t('common_alt_img', { ns: 'common/base' })}
                  className={cx('menuItemIcon')}
                />
              </ListItemIcon>

              <Typography component='p' className={cx('menuItemText', { active: isActive })}>
                {t(name)}
              </Typography>
            </ListItemButton>
          )}
        </NavLink>
      )}

      {/* Dropdown menu */}
      {isSubmenu && (
        <Stack gap={3}>
          <ListItemButton
            id='menuItemComponent'
            className={cx('menuBtn', { active: hasChildActive })}
            onClick={toggleSubmenu}
          >
            <ListItemIcon className={cx('menuIconWrap', { active: hasChildActive })}>
              <img
                className={cx('menuItemIcon')}
                src={hasChildActive ? activeIcon : icon}
                alt={t('common_alt_img', { ns: 'common/base' })}
              />
            </ListItemIcon>

            <Typography component='p' className={cx('menuItemText', { active: hasChildActive })}>
              {t(name)}
            </Typography>

            <Box component={'div'} className={cx('menuItemPrev')}>
              <Box
                component={'div'}
                className={cx('menuItemPrevIcon', hasChildActive ? 'prevIconActive' : 'prevIconInActive')}
              ></Box>
              <Box
                component={'div'}
                className={cx('menuItemPrevIcon', hasChildActive ? 'prevIconActive' : 'prevIconInActive', {
                  show: openSubmenu,
                })}
              ></Box>
            </Box>
          </ListItemButton>

          <Collapse in={openSubmenu} timeout='auto' unmountOnExit>
            <List component='div' disablePadding>
              {children?.map((submenu: IRouteModel) => {
                const path = submenu?.path ? `/${submenu.path.split('/').filter(Boolean).pop()}` : EMPTY_STRING;

                const isPermission = handleShowSideBarSubmenu(path);

                if (isPermission === false) return null;

                return (
                  <NavLink
                    key={submenu.path}
                    to={submenu.path}
                    end={submenu.index}
                    className={cx('menuLink', 'submenuLink')}
                  >
                    {({ isActive }) => {
                      return (
                        <Typography component='p' className={cx('menuItemTextSub', { active: isActive })}>
                          {t(submenu?.name ?? EMPTY_STRING)}
                        </Typography>
                      );
                    }}
                  </NavLink>
                );
              })}
            </List>
          </Collapse>
        </Stack>
      )}
    </>
  );
};

export default MenuItem;
